import { V2Shell } from "@/v2/components/V2Shell";
import { SidebarLayout } from "@/v2/components/layout/SidebarLayout";
import SentGifts from "./SentGifts";
import { GiftPageErrorBoundary } from "./GiftPageErrorBoundary";
import { useGiftWizard } from "./hooks/useGiftWizard";
import { GiftStepperSidebar } from "./components/GiftStepperSidebar";
import { OrderSummaryRail } from "./components/OrderSummaryRail";
import { StickyCheckoutBar } from "@/v2/components/StickyCheckoutBar";
import { StepChooseGift } from "./components/steps/StepChooseGift";
import { StepRecipientDelivery } from "./components/steps/StepRecipientDelivery";
import { StepPayment } from "./components/steps/StepPayment";
import { StepConfirmation } from "./components/steps/StepConfirmation";

const GiftPageMain = () => {
  const wizard = useGiftWizard();
  const {
    configData,
    channelName,
    step,
    setStep,
    isLoadingData,
    billing,
    setBilling,
    selectedPlan,
    setSelectedPlan,
    termCount,
    setTermCount,
    safeTermMax,
    availableTiers,
    recipientHasSub,
    setRecipientHasSub,
    pricesReady,
    pricesError,
    loadOptions,
    clientSecret,
    returnUrl,
    orderNumber,
    orderDate,
    activeLabel,
    planPrice,
    getMonthlyDisplayPrice,
    getAnnualDisplayPrice,
    formik,
    checkRecipientStatus,
    proceedToPayment,
    stashGiftResume,
    onPaymentSuccess,
    onPaymentError,
    stepsList,
    buyAnotherGift,
    goToSchedule,
    goToAccount,
  } = wizard;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.handleChange(e);
    setRecipientHasSub(false);
  };
  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    checkRecipientStatus(e.target.value);
  };

  return (
    <SidebarLayout
      sidebar={
        <GiftStepperSidebar
          step={step}
          stepsList={stepsList}
          onStepClick={setStep}
          supportUrl={configData?.supportUrl}
        />
      }
    >
      <div className="px-4 pb-16 w-full min-w-0 bg-white">
        {/* Payment is completed inline by the embedded Stripe form (step 3),
            so the native form never submits - it only groups the fields. */}
        <form onSubmit={(e) => e.preventDefault()} className="w-full">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-8 items-start w-full">
            {/* CENTER STEP CONTENT AREA */}
            <div className="xl:col-span-8 2xl:col-span-7 w-full min-w-0">
              {step === 1 && (
                <StepChooseGift
                  channelName={channelName}
                  billing={billing}
                  setBilling={setBilling}
                  termCount={termCount}
                  setTermCount={setTermCount}
                  safeTermMax={safeTermMax}
                  availableTiers={availableTiers}
                  selectedPlan={selectedPlan}
                  setSelectedPlan={setSelectedPlan}
                  pricesReady={pricesReady}
                  pricesError={pricesError}
                  loadOptions={loadOptions}
                  getMonthlyDisplayPrice={getMonthlyDisplayPrice}
                  getAnnualDisplayPrice={getAnnualDisplayPrice}
                />
              )}

              {step === 2 && (
                <StepRecipientDelivery
                  formik={formik}
                  recipientHasSub={recipientHasSub}
                  planPrice={planPrice}
                  pricesReady={pricesReady}
                  onEmailChange={handleEmailChange}
                  onEmailBlur={handleEmailBlur}
                  onBack={() => setStep(1)}
                />
              )}

              {step === 3 && (
                <StepPayment
                  clientSecret={clientSecret}
                  returnUrl={returnUrl}
                  planPrice={planPrice}
                  onBack={() => setStep(2)}
                  onSuccess={onPaymentSuccess}
                  onError={onPaymentError}
                  onBeforeConfirm={stashGiftResume}
                />
              )}

              {step === 4 && (
                <StepConfirmation
                  formik={formik}
                  channelName={channelName}
                  activeLabel={activeLabel}
                  billing={billing}
                  termCount={termCount}
                  orderNumber={orderNumber}
                  orderDate={orderDate}
                  onBuyAnotherGift={buyAnotherGift}
                  onGoToSchedule={goToSchedule}
                  onReturnToAccount={goToAccount}
                />
              )}

              {/* SENT GIFTS LIST BELOW WIZARD */}
              <div id="sent-gifts" className="mt-8 scroll-mt-6">
                <SentGifts />
              </div>
            </div>

            {/* RIGHT ORDER SUMMARY RAIL */}
            <OrderSummaryRail
              step={step}
              logoUrl={configData?.logoUrl}
              channelName={channelName}
              activeLabel={activeLabel}
              billing={billing}
              termCount={termCount}
              formik={formik}
              recipientHasSub={recipientHasSub}
              pricesReady={pricesReady}
              pricesError={pricesError}
              planPrice={planPrice}
              loadOptions={loadOptions}
              isLoadingData={isLoadingData}
              onEditRecipient={() => setStep(2)}
              onContinueStep1={() => setStep(2)}
              onContinueStep2={proceedToPayment}
            />
          </div>

          {/* Clears the fixed mobile checkout bar (steps 1-2 only). */}
          {(step === 1 || step === 2) && (
            <div
              className="lg:hidden"
              style={{ height: 132 }}
              aria-hidden="true"
            />
          )}
        </form>

        {/* Mobile-only sticky total + CTA; steps 1-2. On step 3 the pay button
            lives in the embedded Stripe form, and step 4 is the confirmation. */}
        {(step === 1 || step === 2) && (
          <StickyCheckoutBar
            totalLabel="Total"
            price={
              pricesReady && planPrice > 0
                ? `$${planPrice.toFixed(2)}`
                : undefined
            }
            buttonLabel={step === 1 ? "Continue" : "Continue to Payment"}
            onClick={step === 1 ? () => setStep(2) : proceedToPayment}
            disabled={
              step === 1
                ? !pricesReady || pricesError || planPrice <= 0
                : isLoadingData
            }
          />
        )}
      </div>
    </SidebarLayout>
  );
};

const GiftPage = () => {
  return (
    <V2Shell>
      <GiftPageErrorBoundary>
        <GiftPageMain />
      </GiftPageErrorBoundary>
    </V2Shell>
  );
};

export default GiftPage;
